import React, { useState, useEffect } from "react";
import "./styles/VariationSection.css";

const VariationSection = ({ onVariationsChange, initialVariations = [] }) => {
  // Input for colors (comma separated)
  const [colorsInput, setColorsInput] = useState("");
  // Use the passed initial variations to initialize state
  const [variations, setVariations] = useState(initialVariations);

  // Global options
  const [useGlobalSizes, setUseGlobalSizes] = useState(true);
  const [globalSizesInput, setGlobalSizesInput] = useState("");
  const [useGlobalMaterial, setUseGlobalMaterial] = useState(true);
  const [globalMaterialInput, setGlobalMaterialInput] = useState("");
  const [useGlobalModel, setUseGlobalModel] = useState(true);
  const [globalModelInput, setGlobalModelInput] = useState("");

  // When colorsInput changes, update the variations
  useEffect(() => {
    const colors = colorsInput
      .split(",")
      .map((c) => c.trim())
      .filter(Boolean);
    // Merge new colors with existing variations to avoid losing data
    const newVariations = colors.map((color) => {
      const existing = variations.find((v) => v.color === color);
      return existing || { color, size: [], material: "", model: "" };
    });
    setVariations(newVariations);
    onVariationsChange && onVariationsChange(newVariations);
  }, [colorsInput]);

  // Global options handlers (sizes, material, model)
  useEffect(() => {
    if (useGlobalSizes) {
      const sizesArray = globalSizesInput
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const updatedVariations = variations.map((v) => ({
        ...v,
        size: sizesArray,
      }));
      setVariations(updatedVariations);
      onVariationsChange && onVariationsChange(updatedVariations);
    }
  }, [globalSizesInput, useGlobalSizes]);

  useEffect(() => {
    if (useGlobalMaterial) {
      const updatedVariations = variations.map((v) => ({
        ...v,
        material: globalMaterialInput,
      }));
      setVariations(updatedVariations);
      onVariationsChange && onVariationsChange(updatedVariations);
    }
  }, [globalMaterialInput, useGlobalMaterial]);

  useEffect(() => {
    if (useGlobalModel) {
      const updatedVariations = variations.map((v) => ({
        ...v,
        model: globalModelInput,
      }));
      setVariations(updatedVariations);
      onVariationsChange && onVariationsChange(updatedVariations);
    }
  }, [globalModelInput, useGlobalModel]);

  // Handlers for individual variation changes (when not using global options)
  const handleIndividualSizesChange = (index, value) => {
    const sizesArray = value.split(",").map((s) => s.trim()).filter(Boolean);
    const updated = variations.map((v, i) =>
      i === index ? { ...v, size: sizesArray } : v
    );
    setVariations(updated);
    onVariationsChange && onVariationsChange(updated);
  };

  const handleIndividualMaterialChange = (index, value) => {
    const updated = variations.map((v, i) =>
      i === index ? { ...v, material: value } : v
    );
    setVariations(updated);
    onVariationsChange && onVariationsChange(updated);
  };

  const handleIndividualModelChange = (index, value) => {
    const updated = variations.map((v, i) =>
      i === index ? { ...v, model: value } : v
    );
    setVariations(updated);
    onVariationsChange && onVariationsChange(updated);
  };

  return (
    <div className="variation-section">
      <h3>
        Variations
        <span className="subtext">
          (Enter colors first, then specify sizes, material, and model)
        </span>
      </h3>
      {/* Colors Input */}
      <div className="input-group">
        <input
          type="text"
          placeholder="Enter colors (comma-separated)"
          value={colorsInput}
          onChange={(e) => setColorsInput(e.target.value)}
          className="input-field"
        />
      </div>

      {/* Global Options for Sizes, Material, and Model */}
      <div className="global-options">
        <div className="global-option">
          <input
            type="checkbox"
            id="global-sizes"
            checked={useGlobalSizes}
            onChange={() => setUseGlobalSizes(!useGlobalSizes)}
          />
          <label htmlFor="global-sizes">Use same sizes for all colors</label>
          {useGlobalSizes && (
            <div className="input-group">
              <input
                type="text"
                placeholder="Enter sizes (comma-separated)"
                value={globalSizesInput}
                onChange={(e) => setGlobalSizesInput(e.target.value)}
                className="input-field"
              />
            </div>
          )}
        </div>

        <div className="global-option">
          <input
            type="checkbox"
            id="global-material"
            checked={useGlobalMaterial}
            onChange={() => setUseGlobalMaterial(!useGlobalMaterial)}
          />
          <label htmlFor="global-material">
            Use same material for all colors
          </label>
          {useGlobalMaterial && (
            <div className="input-group">
              <input
                type="text"
                placeholder="Enter material"
                value={globalMaterialInput}
                onChange={(e) => setGlobalMaterialInput(e.target.value)}
                className="input-field"
              />
            </div>
          )}
        </div>

        <div className="global-option">
          <input
            type="checkbox"
            id="global-model"
            checked={useGlobalModel}
            onChange={() => setUseGlobalModel(!useGlobalModel)}
          />
          <label htmlFor="global-model">
            Use same model for all colors
          </label>
          {useGlobalModel && (
            <div className="input-group">
              <input
                type="text"
                placeholder="Enter model"
                value={globalModelInput}
                onChange={(e) => setGlobalModelInput(e.target.value)}
                className="input-field"
              />
            </div>
          )}
        </div>
      </div>

      {/* Variations List */}
      <div className="variations-list">
        {variations.map((variation, index) => (
          <div key={index} className="variation-card">
            <h5>{variation.color}</h5>
            {/* Sizes Input */}
            {!useGlobalSizes && (
              <div className="input-group">
                <input
                  type="text"
                  placeholder={`Enter sizes for ${variation.color} (comma-separated)`}
                  value={variation.size.join(", ")}
                  onChange={(e) =>
                    handleIndividualSizesChange(index, e.target.value)
                  }
                  className="input-field"
                />
              </div>
            )}
            {/* Material Input */}
            {!useGlobalMaterial && (
              <div className="input-group">
                <input
                  type="text"
                  placeholder={`Enter material for ${variation.color}`}
                  value={variation.material}
                  onChange={(e) =>
                    handleIndividualMaterialChange(index, e.target.value)
                  }
                  className="input-field"
                />
              </div>
            )}
            {/* Model Input */}
            {!useGlobalModel && (
              <div className="input-group">
                <input
                  type="text"
                  placeholder={`Enter model for ${variation.color}`}
                  value={variation.model}
                  onChange={(e) =>
                    handleIndividualModelChange(index, e.target.value)
                  }
                  className="input-field"
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default VariationSection;
